import React, {
  useMemo,
  useEffect,
  useState,
  useContext,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import Modal from 'react-modal';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import CloseIcon from '@wix/da-ds/pkg/Icons/24x24/Close';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { ScreenSizeContext } from '@wix/da-hooks/pkg/useMeasureElement/ScreenSizeContext';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';
import { parse as parseSrcSet } from '../../utils/srcset';
import { WIDTH_TO_HEIGHT_RATIO_THRESHOLD_TO_CROP_SUPER_TALL } from '../constants';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';

import s from './ZoomModal.scss';

function isBiggerThanViewport(width?: number, height?: number) {
  if (typeof window === 'undefined' || !width || !height) {
    return false;
  }

  return width > window.innerWidth || height > window.innerHeight;
}

enum ZoomStage {
  INITIAL,
  EXPANDED,
}

export interface Props {
  src: string;
  srcSet?: string;
  width?: number;
  height?: number;
  isPixel: boolean;
  onClose: () => void;
}

export const ZoomModal: React.FC<Props> = ({
  src,
  srcSet,
  width,
  height,
  isPixel,
  onClose,
}) => {
  const { t } = useTranslation();
  const [zoomStage, setZoomStage] = useState(ZoomStage.INITIAL);
  const isMobile = useContext(MobileContext);
  const { width: screenWidth } = useContext(ScreenSizeContext);
  const largestImage = useMemo(
    () =>
      parseSrcSet(srcSet || '')
        .concat({ width, height, url: src })
        .filter(source => source.width)
        .sort(({ width: a = 0 }, { width: b = 0 }) => a - b)
        .pop(),
    [srcSet, width, height, src]
  );
  const maxWidth = Math.max(largestImage?.width || 0, screenWidth);
  const canZoom = isBiggerThanViewport(width, height);

  const mobileImageZoom = useFlag('mobile_image_zoom');
  const isSuperTallOnMobile = useMemo(() => {
    if (!mobileImageZoom || !isMobile || !width || !height) {
      return false;
    }
    return width / height < WIDTH_TO_HEIGHT_RATIO_THRESHOLD_TO_CROP_SUPER_TALL;
  }, [mobileImageZoom, isMobile, width, height]);

  // close when escape is used
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const toggleZoomStage = () => {
    if (canZoom) {
      // For mobile, we only want the initial zoom mode, then users can pinch+zoom all they want.
      if (zoomStage === ZoomStage.INITIAL && !isMobile) {
        setZoomStage(ZoomStage.EXPANDED);
      } else {
        setZoomStage(ZoomStage.INITIAL);
      }
    }
  };

  const doClose = useCallback(() => {
    yieldToMain(() => {
      onClose();
    });
  }, [onClose]);

  const isFullyZoomed = canZoom && zoomStage === ZoomStage.EXPANDED;

  // wrapping in a modal to avoid the can of worms with scrolling the underlying page and all
  // NoLeakyScroll only goes that far, and this seems more performant overall
  return (
    <Modal
      className={classnames(
        s['modal'],
        zoomStage === ZoomStage.EXPANDED && s['modal-expanded']
      )}
      isOpen
      overlayClassName={s['overlay']}
      onRequestClose={doClose}
    >
      <button
        className={s['unzoom-button']}
        aria-label={t('deviationview.zoomModal.closeButton.altLabel')}
        onClick={doClose}
      >
        <CloseIcon size={IconSize.BIG} className={s['unzoom-button-icon']} />
      </button>

      <div
        className={classnames(
          s['zoom-viewport'],
          isSuperTallOnMobile && s['zoom-viewport-super-tall']
        )}
      >
        <img
          alt=""
          className={classnames(
            s['image'],
            isPixel && s['pixel'],
            isSuperTallOnMobile && s['image-super-tall'],
            canZoom &&
              zoomStage === ZoomStage.INITIAL &&
              s['image-initial-zoom-level'],
            canZoom &&
              zoomStage === ZoomStage.EXPANDED &&
              s['image-expanded-zoom-level']
          )}
          src={isFullyZoomed && largestImage?.url ? largestImage.url : src}
          style={
            isSuperTallOnMobile
              ? { width: Math.min(width!, screenWidth) }
              : isFullyZoomed
                ? { maxWidth }
                : undefined
          }
          onClick={toggleZoomStage}
        />
      </div>
    </Modal>
  );
};
ZoomModal.displayName = 'ZoomModal';

export default ZoomModal;
